import React, { useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { createStructuredSelector } from 'reselect';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import moment from 'moment';
import { Grid } from '@material-ui/core';
import { accountIcons } from 'images';
import { Button, Icon, Div, F } from 'components/Ui';
import {
  ProductListItem,
  CTABanner,
} from 'components/ContentfulFields/ProductCard';
import { isPastCutOffDateTime } from 'utils/common';
import { dataLayerPush } from 'utils/tracking';
import MessageDialog from 'components/Dialogs/MessageDialog';
import {
  selectDeliveryOrderIsFetching,
  selectSelectedServiceLocation,
  selectIsSelectedServiceIndefiniteHold,
} from 'containers/PrimoAccount/selectors';
import { shopDeliveryOrder as shopDeliveryOrderRequest } from '../actions';
import doStyles from './delivery-order-styles.scss';
import {
  getQuantityChangedProductsForDataLayer,
  getRemovedProductsForDataLayer,
} from '../OrderEditor/ExtraDeliveryForm';

const DeliveryOrderForm = ({
  onClose,
  onSubmit,
  isFetching,
  history,
  serviceLocation,
  deliveryOrder,
  onShowSkipDeliveryOrder,
  shopDeliveryOrder,
  onRemoveHold,
  onCancelEditSkipDelivery,
  isIndefiniteHold,
}) => {
  const order = deliveryOrder.items;
  const [editedOrder, setEditedOrder] = useState(order);
  const [hasChanges, setHasChanges] = useState(false);
  const [editSkipDelivery, setEditSkipDelivery] = useState(false);
  const skip = !editSkipDelivery && !!deliveryOrder.skipDelivery;

  const [openMessageDialog, setOpenMessageDialog] = useState(false);
  const cannotDeleteTitle = 'Oops!';
  const cannotDeleteMessage =
    'Looks like you’re trying to remove all the items from this delivery. If you need to skip this delivery, click on the skip delivery button instead.';

  const onDelete = (deletedProduct) => {
    if (editedOrder.length === 1) {
      setOpenMessageDialog(true);
    } else {
      setHasChanges(true);
      setEditedOrder(
        editedOrder.filter(
          (product) => product.itemNumber !== deletedProduct.itemNumber,
        ),
      );
    }
  };

  const handleQuantityChange = (product, newCount) => {
    setHasChanges(true);
    const newOrder = editedOrder.map((orderedProduct) => {
      if (orderedProduct.itemNumber === product.itemNumber) {
        orderedProduct.originalQuantity = orderedProduct.originalQuantity
          ? orderedProduct.originalQuantity
          : orderedProduct.quantity;
        return { ...orderedProduct, quantity: newCount };
      }
      return orderedProduct;
    });
    setEditedOrder(newOrder);
  };

  const handleSkipDelivery = () => {
    onShowSkipDeliveryOrder();
  };

  const handleShop = () => {
    onClose();
    setEditSkipDelivery(false);
    shopDeliveryOrder(deliveryOrder);
    history.push(
      `/account/products/list?serviceLocationId=${serviceLocation.id}`,
    );
  };

  const handleCancelEditSkipDelivery = () => {
    onCancelEditSkipDelivery();
    setEditSkipDelivery(false);
  };

  const handleRemoveHoldAndSubmit = useCallback(() => {
    setEditSkipDelivery(false);
    if (!isIndefiniteHold) {
      onRemoveHold();
      deliveryOrder.skipDelivery = false;
    }
    handleSubmit();
  });

  const handleSubmit = useCallback(() => {
    onSubmit(editedOrder);

    const section = skip ? 'edit skipped delivery' : 'edit scheduled order';

    const unmappedRemovedProducts = getRemovedProductsForDataLayer(
      order,
      editedOrder,
    );

    const removedProducts = unmappedRemovedProducts || [];

    const changedQuantityProducts = getQuantityChangedProductsForDataLayer(
      order,
      editedOrder,
    );

    if (removedProducts.length) {
      dataLayerPush(
        'SelfServe',
        {
          section,
          event: 'delete',
          products: removedProducts,
        },
        'dlS16',
        'PUSH_LOAD_RECURRING_ORDER_SUCCESS',
      );
    }

    if (changedQuantityProducts.length) {
      dataLayerPush(
        'SelfServe',
        {
          section,
          event: 'quantity-change',
          products: changedQuantityProducts,
        },
        'dlS17',
        'PUSH_LOAD_RECURRING_ORDER_SUCCESS',
      );
    }
  });

  const isPastCutOff = isPastCutOffDateTime(deliveryOrder.cutOffDateTime);

  const ctaBanner = isPastCutOff ? null : (
    <div className={doStyles.addButton}>
      <CTABanner
        title="Add to your order"
        ctaText="Shop"
        icon={
          <img src={accountIcons.Plus} alt="" className={doStyles.ctaIcon} />
        }
        onAction={handleShop}
      />
    </div>
  );

  const title = skip ? 'Delivery Skipped:' : 'Delivery Scheduled:';

  const cutoffDate = moment(deliveryOrder.cutOffDateTime).format('MM/DD/YY');
  const subtitle = skip
    ? `This order may be rescheduled until 3pm ET on`
    : `This order may be modified until 3pm ET on`;

  return (
    <div className={doStyles.formWrapper}>
      <div className={doStyles.editForm}>
        <div className={doStyles.formHeader}>
          {!skip && <Icon icon="cog" />}
          {skip && <Icon className={doStyles.skipDeliveryIcon} icon="ban" />}
          <h2>
            <F>{title}</F>
            <br />
            {moment(deliveryOrder.date).format('MMMM D, YYYY')}
          </h2>
          <div className={doStyles.subtext}>
            <F>{subtitle}</F> {cutoffDate}
          </div>
        </div>
        <div className={doStyles.formBody}>
          {!skip && ctaBanner}
          {editedOrder ? (
            editedOrder.map((product) => (
              <ProductListItem
                onQuantityChange={
                  product.deliveryFrequency !== 'Replenish' &&
                  !product.isPPS &&
                  !skip &&
                  !isPastCutOff
                    ? handleQuantityChange
                    : null
                }
                removable
                onDelete={
                  !product.isPPS && product.removable && !skip && !isPastCutOff
                    ? onDelete
                    : null
                }
                key={product.itemNumber}
                product={product}
                inDrawer
              />
            ))
          ) : (
            <div>Delivery order has nothing</div>
          )}
        </div>
        <Div className={doStyles.note}>
          Note: Prices per item do not reflect bottle deposits and other
          surcharges.
        </Div>
        {!skip && !editSkipDelivery && (
          <div className={doStyles.formBottom}>
            <div className={doStyles.additionalActions}>
              <Button
                className={doStyles.skipDelivery}
                text="Skip Delivery"
                onClick={handleSkipDelivery}
                disabled={isPastCutOff}
                link
              />
            </div>
            <div className={doStyles.formActions}>
              <Button
                className={doStyles.cancelButton}
                text="Cancel Changes"
                onClick={onClose}
                disabled={isFetching || !hasChanges}
              />
              <Button
                className={doStyles.saveButton}
                text="Confirm Changes"
                onClick={handleSubmit}
                disabled={isFetching || !hasChanges || editedOrder.length === 0}
              />
            </div>
          </div>
        )}
        {editSkipDelivery && (
          <div className={doStyles.formBottom}>
            <div className={doStyles.formActions}>
              <Button
                className={doStyles.cancelButton}
                text="Cancel"
                onClick={handleCancelEditSkipDelivery}
                disabled={isFetching}
              />
              <Button
                className={doStyles.saveButton}
                text="Confirm"
                onClick={handleRemoveHoldAndSubmit}
                disabled={isFetching || order.length === 0}
              />
            </div>
          </div>
        )}
        {skip && (
          <div className={doStyles.formBottom}>
            <Grid container spacing={1} className={doStyles.gridContainer}>
              <Grid item md={6} className={doStyles.gridLeftItem}>
                <Div className={doStyles.title}>Change your mind?</Div>
                <Div className={doStyles.subtitle}>
                  Rescheduling your skipped delivery is easy. You can edit your
                  order in the next step!
                </Div>
              </Grid>
              <Grid item md={6} className={doStyles.gridRightItem}>
                <Button
                  className={doStyles.editThisOrderButton}
                  text="Edit this order"
                  onClick={() => setEditSkipDelivery(true)}
                  disabled={isPastCutOff}
                  data-tracking="Unskip Order"
                />
              </Grid>
            </Grid>
          </div>
        )}
      </div>
      {openMessageDialog && (
        <MessageDialog
          open={openMessageDialog}
          onClose={() => setOpenMessageDialog(false)}
          title={cannotDeleteTitle}
          message={cannotDeleteMessage}
        />
      )}
    </div>
  );
};

DeliveryOrderForm.propTypes = {
  onClose: PropTypes.func,
  onSubmit: PropTypes.func,
  onShowSkipDeliveryOrder: PropTypes.func,
  onRemoveHold: PropTypes.func,
  onCancelEditSkipDelivery: PropTypes.func,
  isFetching: PropTypes.bool,
  serviceLocation: PropTypes.object,
  isIndefiniteHold: PropTypes.bool,
  history: PropTypes.object,
  deliveryOrder: PropTypes.object,
  shopDeliveryOrder: PropTypes.func,
};

DeliveryOrderForm.defaultProps = {};

const mapStateToProps = createStructuredSelector({
  isFetching: selectDeliveryOrderIsFetching(),
  serviceLocation: selectSelectedServiceLocation(),
  isIndefiniteHold: selectIsSelectedServiceIndefiniteHold(),
});

const mapDispatchToProps = {
  shopDeliveryOrder: shopDeliveryOrderRequest,
};

export default withRouter(
  connect(mapStateToProps, mapDispatchToProps)(DeliveryOrderForm),
);
